import { gql } from "@apollo/client";
import { faCalendarPlus, faCalendarXmark, faHashtag, faPen, faPlus, faPlusSquare } from "@fortawesome/free-solid-svg-icons";
import Button from "components/common/button/Button";
import ConfirmationModal from "components/common/confirmation/ConfirmationModal";
import Datalist, { DatalistPropsType } from "components/common/datalist/Datalist";
import Page from "components/common/page/Page";
import Translate from "components/common/Translate";
import ApiTokenForm from "components/entities/app/api-token/ApiTokenForm";
import ModalPortal from "components/modal/ModalPortal";
import { uuid } from "config/app-constants";
import { api, graph } from "config/axios-context";
import { useRef } from "react";
import { ApiTokenDataListSettingsPageQuery, ApiTokenDataListSettingsPageQueryVariables, ApiTokenFilterInput, ApiTokenSortInput, SortEnumType } from "types/graphql";
import { appendFilters, Conditions } from "utilities/datalist/datalist-filter-utilities";
import { DateFormats, DateUtil } from "utilities/date-utilities";
import useAppContext from "utilities/hooks/useAppContext";

const query = gql`
query ApiTokenDataListSettingsPage($where: ApiTokenFilterInput, $take: Int, $skip: Int, $order: [ApiTokenSortInput!]){
    apiTokens {
        items (where: $where, take: $take, skip: $skip, order: $order) {
            name,
            createdDate,
            expiredAt,
            id,
            createdBy {
                name
            }
            updatedBy {
                name
            }
        }
        count (where: $where)
    }
}
`

type DataItem = NonNullable<ApiTokenDataListSettingsPageQuery['apiTokens']>["items"][0];

const ApiTokenPage = () => {
    const context = useAppContext();
    const dataListRef = useRef<Datalist<DataItem, ApiTokenFilterInput, ApiTokenSortInput>>(null);

    const dataListConfig: DatalistPropsType<DataItem, ApiTokenFilterInput, ApiTokenSortInput> = {
        name: 'apiTokens-settings-page',
        itemId(i) {
            return i.id;
        },
        dataSource: {
            request: async params => {
                var variables: ApiTokenDataListSettingsPageQueryVariables = {
                    where: params.filters,
                    take: params.take,
                    skip: params.skip,
                    order: params.sort
                }

                if (params.searchText) {
                    const search: ApiTokenFilterInput = {
                        or: [
                            { name: { contains: params.searchText } },
                            {
                                createdBy: {
                                    name: {
                                        contains: params.searchText
                                    }
                                }
                            },
                            {
                                updatedBy: {
                                    name: {
                                        contains: params.searchText
                                    }
                                }
                            }
                        ]
                    }

                    variables.where = appendFilters(variables.where, search);
                }

                try {
                    const response = await graph<ApiTokenDataListSettingsPageQuery, ApiTokenDataListSettingsPageQueryVariables>({
                        query: query,
                        variables
                    });

                    if (response.errors) {
                        throw response.errors;
                    }

                    return {
                        count: response.data?.apiTokens?.count || 0,
                        items: response.data?.apiTokens?.items || []
                    };
                } catch (error: any) {
                    context.notify('error', 'error.loading-data', Array.isArray(error) ? error[0].message : error.message);
                    throw error;
                }
            },
            sort: [
                {
                    name: 'createdDate',
                    direction: SortEnumType.Desc,
                    id: uuid()
                }
            ],
            filter: {
                id: uuid(),
                logic: 'or',
                filters: [
                    {
                        name: 'expiredAt',
                        value: new Date(),
                        condition: Conditions.gt,
                        id: uuid()
                    },
                    {
                        name: 'expiredAt',
                        value: null,
                        condition: Conditions.eq,
                        id: uuid()
                    }
                ]  
            }
        },
        table: {
            columns: [
                { // name
                    name: 'name',
                    title: 'fields.name.name',
                    sort: true,
                    filter: {
                        type: 'string'
                    },
                    card: {
                        icon: faHashtag,
                        group: 'main'
                    }
                },
                { // createdDate
                    name: 'createdDate',
                    title: 'fields.createdDate.name',
                    sort: true,
                    type: 'date',
                    resolve(i) {
                        return DateUtil.format(i.createdDate, DateFormats.LONG);
                    },
                    filter: {
                        type: 'date'
                    },
                    card: {
                        icon: faCalendarPlus,
                        group: 'main'
                    }
                },
                { // expiredAt
                    name: 'expiredAt',
                    title: 'fields.expiredAt.name',
                    sort: true,
                    type: 'date',
                    filter: {
                        type: 'nullDate'
                    },
                    card: {
                        icon: faCalendarXmark,
                        group: 'main'
                    },
                    resolve(i) {
                        return i.expiredAt ? DateUtil.format(i.expiredAt, DateFormats.LONG) : '-';
                    }
                },
                { // createdBy
                    name: 'createdBy',
                    title: 'fields.createdBy.name',
                    sort: true,
                    filter: {
                        type: 'reference'
                    },
                    resolve(i) {
                        return i.createdBy.name;
                    },
                    card: {
                        icon: faPlusSquare,
                        group: 'user'
                    }
                },
                { // updatedBy
                    name: 'updatedBy',
                    title: 'fields.updatedBy.name',
                    sort: true,
                    filter: {
                        type: 'nullReference'
                    },
                    resolve(i) {
                        return i.updatedBy?.name || '-';
                    },
                    card: {
                        icon: faPen,
                        group: 'user'
                    }
                },
                {// actions
                    name: 'actions',
                    title: '',
                    resolve(i) {
                        return i.expiredAt && new Date(i.expiredAt) <= new Date()
                            ? <></>
                            : <Button onClick={() => disable(i)} theme="link" title={context.t('button.remove')} className="text-danger">
                                <Translate i18nKey="button.remove" />
                            </Button>
                    },
                    card: {
                        group: 'actions'
                    }
                }
            ]
        },
        header: {
            rightButtons: [
                {
                    title: 'button.add',
                    icon: faPlus,
                    onClick: () => {
                        context.modal.open('add-new-api-token-settings-page');
                    },
                    name: 'add-new-api-token-settings-page'
                }
            ]
        }
    }

    const disable = (dataItem: DataItem) => {
        context.modal.open('disable-api-token-settings-page-confiramtion', dataItem.id);
    }

    const disableConfirmed = async (id: any) => {
        try {
            const response = await api.put('api/DisableApiToken?tokenId=' + id);
            if (!response.success) {
                throw response.statusText;
            }

            context.notify('success', 'form.submit.success');
            dataListRef.current?.reload();
            context.navigate(-1);
        } catch (error) {
            console.error(error);
            context.notify('error', 'form.submit.error');
        }
    }

    return <>
        <Page title="Api token" wFull>
            <Datalist {...dataListConfig} ref={dataListRef} />
        </Page>
        <ModalPortal name="add-new-api-token-settings-page" title="button.add" onClose={() => dataListRef.current?.reload()}>
            <ApiTokenForm name="add-new-api-token-settings-page-form" onSaved={() => {
                context.navigate(-1);
                dataListRef.current?.reload();
            }} />
        </ ModalPortal>
        <ConfirmationModal text="" name="disable-api-token-settings-page-confiramtion" onReject={() => context.navigate(-1)} title="button.remove" onConfirm={disableConfirmed} />
    </>;
}

export default ApiTokenPage;
